import React, { useEffect, useState } from 'react';
import { useDisplaySession } from '@/hooks/signage';
import { PairingScreen, ContentRenderer, PlaylistPlayer } from '@/components/signage/player';
import { Monitor, WifiOff, WifiIcon } from 'lucide-react';

/**
 * Signage Player Page
 *
 * A full-screen display player for TVs and digital signage devices.
 * Shows a pairing screen when not connected, then displays content
 * from playlists and schedules configured in the dashboard.
 *
 * Features:
 * - Simple 6-digit pairing code for setup
 * - Real-time content updates via Supabase Realtime
 * - Playlist support with transitions
 * - Multiple content types (images, videos, HTML, dashboards)
 * - Heartbeat for online/offline status tracking
 */
export default function SignagePlayer() {
  const {
    isPaired,
    isLoading,
    isActive,
    pairingCode,
    pairingCodeExpiresAt,
    currentPlaylist,
    currentContent,
    error,
    requestPairingCode,
    session,
  } = useDisplaySession();

  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [swStatus, setSwStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  // Register service worker for offline support
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/signage-sw.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration.scope);
          setSwStatus('ready');

          // Check for updates
          registration.addEventListener('updatefound', () => {
            console.log('Service Worker update found');
          });
        })
        .catch((err) => {
          console.error('Service Worker registration failed:', err);
          setSwStatus('error');
        });
    } else {
      setSwStatus('error');
    }
  }, []);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Pre-cache content when playlist changes
  useEffect(() => {
    if (currentPlaylist && 'serviceWorker' in navigator && navigator.serviceWorker.controller) {
      const contentUrls = currentPlaylist.items
        .map((item) => item.content_item.storage_path || item.content_item.external_url)
        .filter(Boolean);

      if (contentUrls.length > 0) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_CONTENT',
          payload: { urls: contentUrls },
        });
      }
    }
  }, [currentPlaylist]);

  // Request fullscreen on mount
  useEffect(() => {
    const requestFullscreen = async () => {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch {
        // Fullscreen not available or denied
        console.log('Fullscreen not available');
      }
    };

    // Only request fullscreen on user interaction (click anywhere)
    const handleClick = () => {
      requestFullscreen();
      document.removeEventListener('click', handleClick);
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, []);

  // Hide cursor after inactivity
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const showCursor = () => {
      document.body.style.cursor = 'default';
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        document.body.style.cursor = 'none';
      }, 3000);
    };

    document.addEventListener('mousemove', showCursor);
    showCursor();

    return () => {
      document.removeEventListener('mousemove', showCursor);
      clearTimeout(timeout);
      document.body.style.cursor = 'default';
    };
  }, []);

  // Loading state
  if (isLoading) {
    return <LoadingScreen />;
  }

  // Not paired - show pairing screen
  if (!isPaired) {
    return (
      <PairingScreen
        pairingCode={pairingCode}
        expiresAt={pairingCodeExpiresAt}
        error={error}
        onRequestNewCode={requestPairingCode}
      />
    );
  }

  // Paired but inactive
  if (!isActive) {
    return <InactiveScreen displayName={session?.displayName} />;
  }

  // Wrapper to add offline indicator
  const ContentWrapper = ({ children }: { children: React.ReactNode }) => (
    <div className="relative w-full h-full">
      {children}
      {/* Offline indicator */}
      {isOffline && (
        <div className="absolute top-4 right-4 flex items-center gap-2 bg-yellow-500/90 text-yellow-950 px-3 py-1.5 rounded-full text-sm font-medium">
          <WifiOff className="w-4 h-4" />
          Offline Mode
        </div>
      )}
    </div>
  );

  // Show playlist
  if (currentPlaylist && currentPlaylist.items.length > 0) {
    return (
      <ContentWrapper>
        <PlaylistPlayer playlist={currentPlaylist} />
      </ContentWrapper>
    );
  }

  // Show single content item
  if (currentContent) {
    return (
      <ContentWrapper>
        <ContentRenderer content={currentContent} />
      </ContentWrapper>
    );
  }

  // No content assigned - show default screen
  return (
    <ContentWrapper>
      <DefaultScreen displayName={session?.displayName} isOffline={isOffline} />
    </ContentWrapper>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-slate-400">Loading display...</p>
      </div>
    </div>
  );
}

interface ScreenProps {
  displayName?: string;
  isOffline?: boolean;
}

function InactiveScreen({ displayName }: ScreenProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center space-y-6">
        <div className="w-24 h-24 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto">
          <WifiOff className="w-12 h-12 text-slate-500" />
        </div>
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-white">Display Inactive</h1>
          {displayName && (
            <p className="text-slate-400">{displayName}</p>
          )}
          <p className="text-slate-500 text-sm mt-4">
            This display has been deactivated. Please contact your administrator.
          </p>
        </div>
      </div>
    </div>
  );
}

function DefaultScreen({ displayName, isOffline }: ScreenProps) {
  const [time, setTime] = React.useState(new Date());

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <div className="text-center space-y-8">
        {/* Clock */}
        <div className="text-8xl font-light text-white font-mono">
          {time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>

        {/* Date */}
        <div className="text-2xl text-slate-400">
          {time.toLocaleDateString([], {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>

        {/* Display name */}
        <div className="mt-16 flex items-center justify-center gap-3 text-slate-500">
          <Monitor className="w-5 h-5" />
          <span>{displayName || 'Connected Display'}</span>
        </div>

        {/* No content message */}
        <p className="text-slate-600 text-sm">
          No content scheduled. Configure content in your TagInn dashboard.
        </p>
      </div>
    </div>
  );
}
